// Kanıt paneli (M16 T18, DR9, mockup 19 sol): hücre editörünün "Kanıt" sekmesi. Bölücülü liste
// (kart/gölge yok): tür rozeti, dosya adı + konum, boyut, tarama rozeti (TARANIYOR / ENGELLENDİ
// pasif), "kaynak dosya (içe aktarma)" rozeti, bağ hedefleri, Aç (yalnız clean) + Bağı kaldır
// (ConfirmDialog). "Kanıt ekle": yeni dosya (FileDropzone → presign → PUT → commit → seçici) ya da
// müşterinin mevcut dosyası (liste → seçici). Seçici: EvidenceTargetPicker (çoklu gösterge × yıl).
// Bağ değişince `onChanged` → grid yenilenir (evidenceCount). Müşteri (portal) görünümü (T19,
// `portalSetId`): liste/yükleme/bağ `api/portal-kpi.ts`'ten; "mevcut dosya" listesi ve "Bağı kaldır"
// yok (portal ucu yok); `downloadable:false` dosyada "Kaynak: … (danışman dosyası)", Aç yok (C2/DR9).
// TODO-EVIDENCE-01: havuz form kanıtlarını da içerir — "form kanıtı · <soru kodu>" rozeti (mevcut
// dosya listesi + satır). TODO-EVIDENCE-03: satırda dosya bazlı "Saklama" seçimi (iç kullanıcı,
// PATCH /kpi/evidence/:id). TODO-EVIDENCE-05: `error` rozeti "TARAMA HATASI".
// güvenlik/UX taraması 2026-10-08: satır bileşeni EvidenceRow.tsx'e ayrıldı (600 satır sınırı);
// "Aç" adresi istemcide kurulur (S-L2).

import { useCallback, useEffect, useState } from "react";
import type { EvidenceDocument, KpiEvidenceLocator, KpiResolvedIndicator } from "@mahir/shared";
import {
  linkKpiEvidence,
  listClientKpiEvidence,
  listKpiValueEvidence,
  unlinkKpiEvidence,
  updateKpiEvidenceRetention,
  uploadKpiEvidence,
  type KpiLinkedEvidence,
} from "../../api/kpi-evidence";
import {
  linkPortalEvidence,
  listPortalValueEvidence,
  uploadPortalEvidence,
} from "../../api/portal-kpi";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_MAX_BYTES,
  evidenceRejectReason,
  fileKindLabel,
  formatFileSize,
  formatHistoryDate,
  formLinkLabels,
} from "../../lib/kpi-evidence-ui";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { FileDropzone } from "../common/FileDropzone";
import { EvidenceRow, FormEvidenceBadge, type PanelItem } from "./EvidenceRow";
import { EvidenceTargetPicker } from "./EvidenceTargetPicker";
import { btnSmall, inputStyle } from "./kpi-form-styles";

export interface EvidencePanelProps {
  clientId: string;
  /** boş hücrede yok — bağ eklenince satır açılır ve grid yenilenince gelir */
  valueId: string | undefined;
  indicator: KpiResolvedIndicator;
  indicators: KpiResolvedIndicator[];
  years: number[];
  entityId: string;
  year: number;
  readOnly: boolean;
  /** bağ eklendi/kaldırıldı — çağıran `refreshGrid()` */
  onChanged: () => void;
  /** T19 — portal (müşteri) modu: set kimliği; verilirse portal API'si kullanılır */
  portalSetId?: string | undefined;
}

const C = KPI_EVIDENCE_COPY.panel;
type AddMode = null | "new" | "existing";

export function EvidencePanel(props: EvidencePanelProps): JSX.Element {
  const { clientId, valueId, indicator, indicators, years, entityId, year, readOnly, onChanged } =
    props;
  const portalSetId = props.portalSetId;
  const [list, setList] = useState<PanelItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [existing, setExisting] = useState<EvidenceDocument[] | null>(null);
  const [existingSearch, setExistingSearch] = useState("");
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pickFor, setPickFor] = useState<EvidenceDocument | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [unlinkFor, setUnlinkFor] = useState<KpiLinkedEvidence | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!valueId) {
      setList([]);
      return;
    }
    try {
      setLoadError(null);
      setList(
        portalSetId
          ? await listPortalValueEvidence(valueId, portalSetId)
          : await listKpiValueEvidence(valueId),
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : C.loadError);
      setList([]);
    }
  }, [valueId, portalSetId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (addMode !== "existing" || existing !== null || portalSetId) return;
    listClientKpiEvidence(clientId)
      .then(setExisting)
      .catch(() => setExisting([]));
  }, [addMode, existing, clientId]);

  async function handleUpload(files: File[]): Promise<void> {
    const file = files[0];
    if (!file) return;
    setUploadError(null);
    setUploadPct(0);
    try {
      const doc = portalSetId
        ? await uploadPortalEvidence(
            portalSetId,
            file,
            file.type || "application/octet-stream",
            setUploadPct,
          )
        : await uploadKpiEvidence(clientId, file, setUploadPct);
      setUploadPct(null);
      setExisting(null); // mevcut liste bayat
      setPickFor(doc);
    } catch (err) {
      setUploadPct(null);
      setUploadError(err instanceof Error ? err.message : C.uploadFailed);
    }
  }

  async function handleLink(args: {
    targets: { metricId: string; entityId: string; year: number }[];
    locator?: KpiEvidenceLocator;
  }): Promise<void> {
    if (!pickFor) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      const res = portalSetId
        ? await linkPortalEvidence({
            setId: portalSetId,
            evidenceId: pickFor.id,
            targets: args.targets,
            ...(args.locator ? { locator: args.locator } : {}),
          })
        : await linkKpiEvidence({
            evidenceId: pickFor.id,
            targets: args.targets,
            ...(args.locator ? { locator: args.locator } : {}),
          });
      setPickFor(null);
      setAddMode(null);
      setNotice(C.linked(res.linked));
      onChanged();
      await reload();
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : C.linkFailed);
    } finally {
      setLinkBusy(false);
    }
  }

  async function handleRetention(evidenceId: string, retentionPolicy: string): Promise<void> {
    setNotice(null);
    try {
      await updateKpiEvidenceRetention(evidenceId, retentionPolicy);
      setNotice(C.retentionSaved);
      await reload();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : C.retentionSaveFailed);
    }
  }

  async function handleUnlink(): Promise<void> {
    if (!unlinkFor || !valueId) return;
    setUnlinkBusy(true);
    setNotice(null); // P3-29: "✓ N hücreye bağlandı" başarı mesajı bağ kaldırılınca kalmasın
    try {
      await unlinkKpiEvidence(valueId, unlinkFor.id);
      setUnlinkFor(null);
      onChanged();
      await reload();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : C.unlinkFailed);
      setUnlinkFor(null);
    } finally {
      setUnlinkBusy(false);
    }
  }

  const filteredExisting = (existing ?? []).filter(
    (d) =>
      !list?.some((l) => l.id === d.id) &&
      (!existingSearch.trim() ||
        d.fileName.toLocaleLowerCase("tr").includes(existingSearch.toLocaleLowerCase("tr"))),
  );

  return (
    <div data-testid="kpi-evidence-panel">
      {list === null ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.loading}
        </div>
      ) : list.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
          <div style={{ fontWeight: 600, color: "var(--color-text-2)" }}>{C.empty}</div>
          <div>{C.emptyHint}</div>
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {list.map((e) => (
            <EvidenceRow
              key={e.id}
              item={e}
              readOnly={readOnly || !!portalSetId}
              portal={!!portalSetId}
              onUnlink={() => setUnlinkFor(e)}
              onRetention={(p) => void handleRetention(e.id, p)}
            />
          ))}
        </ul>
      )}
      {loadError ? (
        <div role="alert" className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
          {loadError}
        </div>
      ) : null}
      {notice ? (
        <div role="status" className="text-xs mt-2" style={{ color: "var(--color-success)" }}>
          ✓ {notice}
        </div>
      ) : null}

      {!readOnly ? (
        <div className="mt-3">
          {addMode === null ? (
            <button
              type="button"
              data-testid="kpi-evidence-add"
              onClick={() => setAddMode("new")}
              className="w-full text-sm"
              style={{
                border: "1px dashed var(--color-border)",
                borderRadius: 8,
                padding: "10px 12px",
                color: "var(--color-text-3)",
                background: "var(--color-bg)",
                minHeight: 40,
              }}
            >
              {C.add}
            </button>
          ) : (
            <div>
              <div className="flex gap-1 mb-2">
                {(portalSetId ? (["new"] as const) : (["new", "existing"] as const)).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={addMode === m}
                    onClick={() => setAddMode(m)}
                    style={{
                      ...btnSmall,
                      background: addMode === m ? "var(--color-primary)" : "transparent",
                      color: addMode === m ? "var(--color-surface)" : "var(--color-text-2)",
                      borderColor: addMode === m ? "var(--color-primary)" : "var(--color-border)",
                    }}
                  >
                    {m === "new" ? C.addNew : C.addExisting}
                  </button>
                ))}
                <span style={{ flex: 1 }} />
                <button type="button" style={btnSmall} onClick={() => setAddMode(null)}>
                  {C.cancel}
                </button>
              </div>
              {addMode === "new" ? (
                uploadPct !== null ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className="text-xs"
                    style={{ color: "var(--color-text-2)" }}
                  >
                    {C.uploading} %{uploadPct}
                    <div
                      style={{
                        height: 4,
                        borderRadius: 2,
                        background: "var(--color-border)",
                        marginTop: 4,
                      }}
                    >
                      <div
                        style={{
                          width: `${uploadPct.toString()}%`,
                          height: "100%",
                          borderRadius: 2,
                          background: "var(--color-primary)",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <FileDropzone
                      accept={EVIDENCE_ACCEPT}
                      multiple={false}
                      maxFiles={1}
                      maxBytes={EVIDENCE_MAX_BYTES}
                      onFiles={(f) => void handleUpload(f)}
                      rejectReason={evidenceRejectReason}
                      hint={C.uploadHint}
                      label={C.pickLabel}
                    />
                    {uploadError ? (
                      <div
                        role="alert"
                        className="text-xs mt-1"
                        style={{ color: "var(--color-error)" }}
                      >
                        {uploadError}
                      </div>
                    ) : null}
                  </>
                )
              ) : (
                <div>
                  <input
                    aria-label={C.existingSearch}
                    placeholder={C.existingSearch}
                    style={{ ...inputStyle, minHeight: 32, padding: "4px 10px" }}
                    value={existingSearch}
                    onChange={(e) => setExistingSearch(e.target.value)}
                  />
                  <ul
                    style={{
                      listStyle: "none",
                      margin: "6px 0 0",
                      padding: 0,
                      maxHeight: 180,
                      overflowY: "auto",
                    }}
                  >
                    {existing === null ? (
                      <li className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        {C.loading}
                      </li>
                    ) : filteredExisting.length === 0 ? (
                      <li className="text-xs" style={{ color: "var(--color-text-3)" }}>
                        {C.existingEmpty}
                      </li>
                    ) : (
                      filteredExisting.map((d) => (
                        <li key={d.id} style={{ borderTop: "1px solid var(--color-border)" }}>
                          <button
                            type="button"
                            onClick={() => setPickFor(d)}
                            className="w-full text-left text-xs"
                            style={{
                              padding: "6px 4px",
                              minHeight: 32,
                              color: "var(--color-text)",
                            }}
                          >
                            <b style={{ marginRight: 6, color: "var(--color-primary)" }}>
                              {fileKindLabel(d.mimeType, d.fileName)}
                            </b>
                            {d.fileName}
                            <span className="ml-2" style={{ color: "var(--color-text-3)" }}>
                              {formatFileSize(d.sizeBytes)} · {formatHistoryDate(d.uploadedAt)}
                            </span>
                            {formLinkLabels(d.formLinks).map((label) => (
                              <FormEvidenceBadge key={label} label={label} />
                            ))}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      {pickFor ? (
        <EvidenceTargetPicker
          open
          file={{ fileName: pickFor.fileName, mimeType: pickFor.mimeType }}
          indicators={indicators}
          years={years}
          entityId={entityId}
          current={{ metricId: indicator.metricId, year }}
          busy={linkBusy}
          error={linkError}
          onConfirm={(a) => void handleLink(a)}
          onClose={() => {
            if (!linkBusy) {
              setPickFor(null);
              setLinkError(null);
            }
          }}
        />
      ) : null}
      <ConfirmDialog
        open={unlinkFor !== null}
        title={KPI_EVIDENCE_COPY.unlinkDialog.title}
        body={`${unlinkFor?.fileName ?? ""} — ${KPI_EVIDENCE_COPY.unlinkDialog.body}`}
        confirmLabel={KPI_EVIDENCE_COPY.unlinkDialog.confirm}
        tone="danger"
        busy={unlinkBusy}
        onConfirm={() => void handleUnlink()}
        onCancel={() => setUnlinkFor(null)}
      />
    </div>
  );
}
